import { format } from 'date-fns';
import { de, enUS, es } from 'date-fns/locale';
import { Calendar, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';

const DateRangePickerComponent = ({ startDate, endDate, onChange, minDate }) => {
  const { t, language } = useTranslation();
  
  // Obtener el locale correcto según el idioma
  const getLocale = () => {
    switch (language) {
      case 'es': return es;
      case 'en': return enUS;
      case 'de': return de;
      default: return es;
    }
  };
  
  const locale = getLocale();
  const [localStartDate, setLocalStartDate] = useState('');
  const [localEndDate, setLocalEndDate] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Sincronizar el valor con las props
  useEffect(() => {
    if (startDate) {
      setLocalStartDate(format(new Date(startDate), 'yyyy-MM-dd'));
    } else {
      setLocalStartDate('');
    }
    if (endDate) {
      setLocalEndDate(format(new Date(endDate), 'yyyy-MM-dd'));
    } else {
      setLocalEndDate('');
    }
  }, [startDate, endDate]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setLocalStartDate(newStartDate);
    
    // Si la fecha de fin es anterior a la nueva fecha de inicio, ajustarla
    if (newStartDate && localEndDate && new Date(localEndDate) < new Date(newStartDate)) {
      setLocalEndDate(newStartDate);
      if (newStartDate) {
        onChange(newStartDate, newStartDate);
      }
    } else if (newStartDate && localEndDate) {
      onChange(newStartDate, localEndDate);
    }
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    setLocalEndDate(newEndDate);
    
    if (!newEndDate) return;

    // Si el usuario elige una fecha de fin anterior al inicio actual,
    // ajustamos el inicio automáticamente para no bloquear la UI.
    if (localStartDate && new Date(newEndDate) < new Date(localStartDate)) {
      setLocalStartDate(newEndDate);
      onChange(newEndDate, newEndDate);
      return;
    }

    if (localStartDate) {
      onChange(localStartDate, newEndDate);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setLocalStartDate('');
    setLocalEndDate('');
    setIsOpen(false);
    // Al limpiar, también debemos limpiar el estado padre para que el usuario
    // pueda seleccionar un rango anterior al previamente seleccionado.
    onChange(null, null);
  };

  // Formatear el texto del input
  const getDisplayText = () => {
    if (localStartDate && localEndDate) {
      const start = format(new Date(localStartDate), 'dd/MM/yyyy', { locale });
      const end = format(new Date(localEndDate), 'dd/MM/yyyy', { locale });
      return `${start} - ${end}`;
    }
    if (localStartDate) {
      const start = format(new Date(localStartDate), 'dd/MM/yyyy', { locale });
      return `${start} - ...`;
    }
    return t('daterangepicker.select.range');
  };

  const getMinStartDate = () => {
    return minDate ? format(new Date(minDate), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
  };

  const getMinEndDate = () => {
    // Permitir elegir fin anterior y auto-ajustar inicio en handleEndDateChange
    return getMinStartDate();
  };

  return (
    <div className="relative date-range-picker-wrapper md:w-full" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-primary text-2c3e50 rounded-lg px-3 py-2.5 text-sm datepicker-trigger outline-none transition-all duration-200 focus:border-primary focus:ring-1 focus:ring-primary hover:border-primary/80 flex items-center gap-2 text-left"
      >
        <Calendar size={16} className="text-primary flex-shrink-0" />
        <span className="flex-1 truncate">{getDisplayText()}</span>
        {localStartDate && localEndDate && (
          <button
            type="button"
            onClick={handleClear}
            className="flex-shrink-0 text-6c757d hover:text-2c3e50 transition-colors"
            aria-label={t('daterangepicker.clear.dates')}
          >
            <X size={14} />
          </button>
        )}
      </button>
      
      {isOpen && (
        <div className="datepicker-dropdown absolute top-full left-0 right-0 md:right-auto mt-2 z-30 border border-primary rounded-lg shadow-xl p-4 min-w-0 w-full md:w-[360px] md:min-w-[360px] bg-white/95 md:bg-white backdrop-blur-sm md:backdrop-blur-none">
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-2c3e50">{t('daterangepicker.select.dates')}</h4>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-6c757d hover:text-2c3e50 transition-colors"
                aria-label={t('daterangepicker.close')}
              >
                <X size={16} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="block text-xs font-medium text-2c3e50">{t('daterangepicker.start.date')}</label>
                <input
                  type="date"
                  className="datepicker-input w-full border border-primary/50 text-2c3e50 rounded-lg px-3 py-2 text-sm outline-none transition-all duration-200 focus:border-primary focus:ring-1 focus:ring-primary"
                  value={localStartDate}
                  onChange={handleStartDateChange}
                  min={getMinStartDate()}
                />
                <small className="text-xs text-6c757d">9:00 AM</small>
              </div>
              
              <div className="space-y-1">
                <label className="block text-xs font-medium text-2c3e50">{t('daterangepicker.end.date')}</label>
                <input
                  type="date"
                  className="datepicker-input w-full border border-primary/50 text-2c3e50 rounded-lg px-3 py-2 text-sm outline-none transition-all duration-200 focus:border-primary focus:ring-1 focus:ring-primary"
                  value={localEndDate}
                  onChange={handleEndDateChange}
                  min={getMinEndDate()}
                />
                <small className="text-xs text-6c757d">21:00 PM</small>
              </div>
            </div>
            
            {localStartDate && localEndDate && (
              <div className="pt-2 border-t border-primary/20">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-6c757d">{t('daterangepicker.duration')}</span>
                  <span className="font-semibold text-2c3e50">
                    {Math.ceil((new Date(localEndDate) - new Date(localStartDate)) / (1000 * 60 * 60 * 24)) + 1} {t('date.days')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePickerComponent;
